import { type ReactNode, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { usePrefsStore } from "@music-app/persistence";
import { FeedbackModal } from "../components/FeedbackModal.js";
import { EarwiseWordmark } from "../components/EarwiseWordmark.js";

export function AppLayout(): ReactNode {
  const location = useLocation();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const guitarEnabled = usePrefsStore((s) => s.enabledInstrumentTracks.includes("guitar"));

  const navItems: Array<{ to: string; label: string; end?: boolean }> = [
    { to: "/review", label: "Today", end: true },
    { to: "/exercise", label: "Ear Training" },
    ...(guitarEnabled ? [{ to: "/guitar", label: "Guitar" }] : []),
    { to: "/learn", label: "Learn" },
    { to: "/progress", label: "Progress" },
    { to: "/settings", label: "Settings" },
    { to: "/about", label: "About" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-ink focus:text-paper"
      >
        Skip to main content
      </a>
      <header className="border-b border-ink/10 bg-paper">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-6">
          <NavLink to="/review" className="font-serif text-xl font-medium text-ink hover:text-accent transition-colors">
            <EarwiseWordmark />
            <span aria-hidden="true" className="text-accent italic ml-1">♭</span>
          </NavLink>
          <nav className="flex items-center gap-1" aria-label="Main">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `px-3 py-1.5 text-sm transition-colors ${
                    isActive
                      ? "text-accent font-medium"
                      : "text-inkSoft hover:text-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main id="main-content" className="flex-1 max-w-5xl w-full mx-auto px-6 py-8">
        <Outlet />
      </main>
      <footer className="border-t border-ink/10 bg-paper">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-baseline justify-between gap-4 text-xs text-inkFaded">
          <span className="font-serif">
            <EarwiseWordmark />
            <span className="italic"> · prototype</span>
          </span>
          <div className="flex items-baseline gap-4">
            <NavLink to="/library" className="hover:text-ink transition-colors font-mono">
              library
            </NavLink>
            <button
              type="button"
              onClick={() => setFeedbackOpen(true)}
              className="hover:text-ink transition-colors font-mono"
            >
              feedback
            </button>
          </div>
        </div>
      </footer>
      {feedbackOpen && (
        <FeedbackModal screen={location.pathname} onClose={() => setFeedbackOpen(false)} />
      )}
    </div>
  );
}
